import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

/* Customer Service inbox.
 *
 * The API answers { message: "SUCCESS", ...data } on 200 and { errorMessage } on
 * a real status code, so every failure path below reads
 * error.response?.data?.errorMessage -- `message` is the SUCCESS envelope's
 * field, not the error's.
 *
 * Nothing here is persisted (see the blacklist in src/store.js): a ticket thread
 * rehydrated from last week's localStorage would render as the current
 * conversation until the first fetch lands.
 */

const readError = (error, fallback) => error?.response?.data?.errorMessage || fallback;

export const supportTicketsSlice = createSlice({
    name: "supportTickets",
    initialState: {
        page: 0,
        pageCount: 0,
        totalRow: 0,
        listData: null,
        listLoading: false,
        countsData: null,
        detailData: null,
        detailLoading: false,
        success: false,
        fail: false,
        errorMessage: null,
    },
    reducers: {
        doGetSupportTicketsListStart: (state) => {
            state.listLoading = true;
        },
        doGetSupportTicketsListSuccess: (state, action) => {
            state.listLoading = false;
            state.listData = action.payload.data;
            state.page = action.payload?.current_page;
            state.pageCount = action.payload?.page_count;
            state.totalRow = action.payload?.total_row;
        },
        doGetSupportTicketsListFail: (state, action) => {
            state.listLoading = false;
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalRow = 0;
            state.errorMessage = action.payload;
        },
        doGetSupportTicketsCountsSuccess: (state, action) => {
            state.countsData = action.payload?.data;
        },
        doGetSupportTicketsCountsFail: (state) => {
            // Deliberately keeps the last good counts: a tab strip that blanks on
            // one failed request is worse than one showing numbers ten seconds old.
            state.countsData = state.countsData || null;
        },
        doGetSupportTicketDetailStart: (state) => {
            state.detailLoading = true;
        },
        doGetSupportTicketDetailSuccess: (state, action) => {
            state.detailLoading = false;
            state.detailData = action.payload?.data;
        },
        doGetSupportTicketDetailFail: (state, action) => {
            state.detailLoading = false;
            state.detailData = null;
            state.errorMessage = action.payload;
        },
        // The polling refresh. Same payload, but it must never blank the thread
        // the agent is reading, so a failed poll changes nothing at all.
        doRefreshSupportTicketDetailSuccess: (state, action) => {
            state.detailData = action.payload?.data;
        },
        doSupportTicketActionSuccess: (state) => {
            state.success = true;
            state.fail = false;
            state.errorMessage = null;
        },
        doSupportTicketActionFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorMessage = action.payload;
        },
        resetSupportTicketsStateSuccess: (state) => {
            state.success = false;
            state.fail = false;
            state.errorMessage = null;
        },
        resetSupportTicketDetailSuccess: (state) => {
            state.success = false;
            state.fail = false;
            state.errorMessage = null;
            state.detailData = null;
            state.detailLoading = false;
        },
    },
});

/* GET /api/admin/support-tickets
   page and size are REQUIRED by the server's query schema, so they are always
   sent; every other param is omitted when empty rather than sent blank. */
export const doGetSupportTicketsList = (param = {}) => async (dispatch) => {
    try {
        dispatch(doGetSupportTicketsListStart());

        let url = "/api/admin/support-tickets?page=" + (param.page || 1) + "&size=" + (param.limit || 15);
        if (param.status) url += "&status=" + encodeURIComponent(param.status);
        if (param.category) url += "&category=" + encodeURIComponent(param.category);
        if (param.queue) url += "&queue=" + encodeURIComponent(param.queue);
        if (param.filter) url += "&filter=" + encodeURIComponent(param.filter);

        const response = await axios.get(url);
        dispatch(doGetSupportTicketsListSuccess(response.data));
        return { success: true };
    } catch (error) {
        dispatch(doGetSupportTicketsListFail(readError(error, "Failed to load support tickets")));
        return { success: false };
    }
};

/* GET /api/admin/support-tickets/counts -- one call feeds all five tab counts and
   the four stat cards, which is why no tab ever counts list.length. */
export const doGetSupportTicketsCounts = () => async (dispatch) => {
    try {
        const response = await axios.get("/api/admin/support-tickets/counts");
        dispatch(doGetSupportTicketsCountsSuccess(response.data));
        return { success: true };
    } catch (error) {
        dispatch(doGetSupportTicketsCountsFail());
        return { success: false };
    }
};

/* GET /api/admin/support-tickets/:id
   `silent` is the polling path: it skips the loading flag and, on failure, leaves
   the thread on screen untouched. */
export const doGetSupportTicketDetail = (param) => async (dispatch) => {
    try {
        if (!param.silent) dispatch(doGetSupportTicketDetailStart());

        const response = await axios.get("/api/admin/support-tickets/" + param.id);
        dispatch(
            param.silent
                ? doRefreshSupportTicketDetailSuccess(response.data)
                : doGetSupportTicketDetailSuccess(response.data)
        );
        return { success: true };
    } catch (error) {
        const message = readError(error, "Failed to load this ticket");
        if (!param.silent) dispatch(doGetSupportTicketDetailFail(message));
        return { success: false, error: message };
    }
};

/* POST /api/admin/support-tickets/:id/messages -- text only; staff replies carry
   no attachments. 409 when the ticket is locked. */
export const doReplySupportTicket = (param) => async (dispatch) => {
    try {
        const response = await axios.post("/api/admin/support-tickets/" + param.id + "/messages", {
            body: param.body,
        });
        dispatch(doSupportTicketActionSuccess(response.data));
        return { success: true };
    } catch (error) {
        const message = readError(error, "Failed to send the reply");
        dispatch(doSupportTicketActionFail(message));
        return { success: false, error: message };
    }
};

/* PUT /api/admin/support-tickets/:id/status -- the server rejects an illegal move
   with 409 while holding the row lock, so a second agent is told rather than
   silently overwriting the first one. */
export const doUpdateSupportTicketStatus = (param) => async (dispatch) => {
    try {
        const body = { status: param.status };
        if (param.note) body.note = param.note;

        const response = await axios.put("/api/admin/support-tickets/" + param.id + "/status", body);
        dispatch(doSupportTicketActionSuccess(response.data));
        return { success: true };
    } catch (error) {
        const message = readError(error, "Failed to update the ticket status");
        dispatch(doSupportTicketActionFail(message));
        return { success: false, error: message };
    }
};

/* PUT /api/admin/support-tickets/:id/assignee -- pass null to send the ticket
   back to the shared queue. */
export const doUpdateSupportTicketAssignee = (param) => async (dispatch) => {
    try {
        const assignedTo = param.assigned_to === undefined ? null : param.assigned_to;
        const response = await axios.put("/api/admin/support-tickets/" + param.id + "/assignee", {
            assigned_to: assignedTo,
        });
        dispatch(doSupportTicketActionSuccess(response.data));
        return { success: true };
    } catch (error) {
        const message = readError(error, "Failed to update the assignee");
        dispatch(doSupportTicketActionFail(message));
        return { success: false, error: message };
    }
};

export const resetSupportTicketsState = () => async (dispatch) => {
    dispatch(resetSupportTicketsStateSuccess());
};

export const resetSupportTicketDetail = () => async (dispatch) => {
    dispatch(resetSupportTicketDetailSuccess());
};

export const {
    doGetSupportTicketsListStart,
    doGetSupportTicketsListSuccess,
    doGetSupportTicketsListFail,
    doGetSupportTicketsCountsSuccess,
    doGetSupportTicketsCountsFail,
    doGetSupportTicketDetailStart,
    doGetSupportTicketDetailSuccess,
    doGetSupportTicketDetailFail,
    doRefreshSupportTicketDetailSuccess,
    doSupportTicketActionSuccess,
    doSupportTicketActionFail,
    resetSupportTicketsStateSuccess,
    resetSupportTicketDetailSuccess,
} = supportTicketsSlice.actions;

export const getSupportTicketsList = (state) => state.supportTickets?.listData;
export const getSupportTicketsListPage = (state) => state.supportTickets?.page;
export const getSupportTicketsListPageCount = (state) => state.supportTickets?.pageCount;
export const getSupportTicketsListTotal = (state) => state.supportTickets?.totalRow;
export const getSupportTicketsListLoading = (state) => state.supportTickets?.listLoading;
export const getSupportTicketsCounts = (state) => state.supportTickets?.countsData;
export const getSupportTicketDetail = (state) => state.supportTickets?.detailData;
export const getSupportTicketDetailLoading = (state) => state.supportTickets?.detailLoading;
export const getSupportTicketsError = (state) => state.supportTickets?.errorMessage;
export const getSupportTicketsSuccess = (state) => state.supportTickets?.success;
export const getSupportTicketsFail = (state) => state.supportTickets?.fail;

export default supportTicketsSlice.reducer;
